import EChartCommon, {
  getAxisPointer,
} from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import {
  formatTimeXAxisText,
  getChartPosition,
} from '@/pages/opinion/utils/chart';
import { echartsContent } from '@/pages/opinion/utils';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { useIntl } from 'umi';
import { NO_DATE } from '@/pages/opinion/constants';
import {
  eChartPercentageYAxis,
  percentageFormat,
  reviewsFormat,
} from '@/pages/opinion/utils/value';
import './index.scss';
const seriesDataKeys = (isRecent = true) => {
  const text = isRecent ? 'Recent' : 'All';
  return [
    {
      text: `${text} Reviews`,
      type: 'bar',
      color: '#5086F3',
    },
    {
      text: `${text} Reviews Positive%`,
      type: 'line',
      color: '#4DAB7D',
    },
  ];
};
export default function ReviewsTimeline({
  filter,
  data,
  loading,
  isRecent,
  height = 260,
  ...cardProps
}: any) {
  const { formatMessage } = useIntl();

  function isPositive(text: string) {
    return text.includes('Positive%');
  }

  const options = useMemo(() => {
    const seriesData = seriesDataKeys(isRecent);
    const legends: { name: string; itemStyle: { color: string } }[] =
      seriesData.map((item) => {
        return {
          name: item.text,
          itemStyle: { color: item.color },
        };
      });
    const series: { name: string; type: string; data: any[] }[] =
      seriesData.map((item, index) => {
        return {
          name: item.text,
          type: item.type,
          itemStyle: { color: item.color },
          data: [],
          yAxisIndex: index,
        };
      });
    const xAxis: string[] = [];

    data.forEach((item: any) => {
      series[0].data.push(item.review || null);
      series[1].data.push(item.score || null);
      xAxis.push(item.time);
    });

    const currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any] },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 3),
        appendToBody: false,
        confine: true,
        formatter: (params: any) => {
          const title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filter.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return `<span>${isPositive(name)
                ? `<img src=${steamPositive} style="width: 18px;height: 18px; vertical-align: middle;margin-right:4px" />`
                : ''
                }${formatMessage({
                  id: params.seriesName || NO_DATE.EMPTY,
                })}</span>`;
            },
            valueFormat: (value, ele) => {
              if (!isPositive(ele.seriesName)) {
                return reviewsFormat(value);
              }
              return percentageFormat(value * 100, '-', 0);
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter?.date_type),
      ),
      grid: {
        top: 10,
        left: 20,
        right: 20,
        containLabel: true,
      },
      legend: {
        data: legends,
        formatter: (name: string) => {
          const newName = formatMessage({ id: name || NO_DATE.EMPTY });
          if (isPositive(name)) {
            return `{positive|} ${newName}`;
          }
          return newName;
        },
        textStyle: {
          color: '#888888',
          fontSize: 12,
          height: 14,
          lineHeight: 14,
          rich: {
            positive: {
              height: 18,
              width: 18,
              paddingRight: 4,
              backgroundColor: {
                image: steamPositive,
              },
            },
          },
        },
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          formatter: (value: string) =>
            formatTimeXAxisText(value, filter?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: 0,
          axisLabel: {
            formatter: (value: any) => {
              return echartsContent(value);
            },
          },
        },
        {
          type: 'value',
          max: 1,
          min: 0,
          axisLabel: {
            formatter: (value: any) => {
              return eChartPercentageYAxis(value);
            },
          },
        },
      ],
      series,
    };

    return currentOptions;
  }, [formatMessage, data]);

  const noData = useMemo(() => {
    return (
      data.length === 0 ||
      !data.some((item: any) => item.review > 0 || item.score > 0)
    );
  }, [data]);

  return (
    <EChartCommon
      options={options}
      loading={loading}
      height={height}
      empty={noData}
      {...cardProps}
      optionsProps={{ type: 'bar' }}
      leftTitle={formatMessage({ id: 'Mentions' })}
      rightTitle={
        <>
          <img src={steamPositive}></img>
          {formatMessage({ id: 'Positive%' })}
        </>
      }
    ></EChartCommon>
  );
}
